import React, { useState, useEffect } from 'react';
import { FileText, Users, Package, UserCheck, Activity, Clock } from 'lucide-react';
import { getRecentActivities, getRelativeTime, ACTIVITY_TYPES } from '../lib/activityService';

const RecentActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      setLoading(true);
      setError('');
      const recentActivities = await getRecentActivities(5);
      setActivities(recentActivities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      setError('Error al cargar la actividad reciente');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      [ACTIVITY_TYPES.ORDER_CREATED]: { Icon: FileText, color: 'text-blue-600' },
      [ACTIVITY_TYPES.ORDER_UPDATED]: { Icon: FileText, color: 'text-blue-500' },
      [ACTIVITY_TYPES.ORDER_STATUS_CHANGED]: { Icon: FileText, color: 'text-blue-700' },
      [ACTIVITY_TYPES.ORDER_ARCHIVED]: { Icon: FileText, color: 'text-yellow-600' },
      [ACTIVITY_TYPES.ORDER_UNARCHIVED]: { Icon: FileText, color: 'text-green-600' },
      [ACTIVITY_TYPES.CLIENT_CREATED]: { Icon: Users, color: 'text-green-600' },
      [ACTIVITY_TYPES.CLIENT_UPDATED]: { Icon: Users, color: 'text-green-500' },
      [ACTIVITY_TYPES.EQUIPMENT_CREATED]: { Icon: Package, color: 'text-purple-600' },
      [ACTIVITY_TYPES.EQUIPMENT_UPDATED]: { Icon: Package, color: 'text-purple-500' },
      [ACTIVITY_TYPES.TECHNICIAN_CREATED]: { Icon: UserCheck, color: 'text-orange-600' },
      [ACTIVITY_TYPES.TECHNICIAN_UPDATED]: { Icon: UserCheck, color: 'text-orange-500' },
    };
    
    return iconMap[type] || { Icon: Activity, color: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Actividad Reciente</h2>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md animate-pulse">
              <div className="h-5 w-5 bg-gray-300 dark:bg-gray-600 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-1"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Actividad Reciente</h2>
        <div className="text-center py-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          <button
            onClick={fetchRecentActivities}
            className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Actividad Reciente</h2>
      </div>
      
      {activities.length === 0 ? (
        <div className="text-center py-8">
          <Activity className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No hay actividad reciente</p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
            Las acciones aparecerán aquí cuando los usuarios interactúen con el sistema
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => {
            const { Icon, color } = getActivityIcon(activity.type);
            
            return (
              <div key={activity.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                <Icon className={`h-5 w-5 ${color} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {typeof activity.description === 'string' 
                      ? activity.description 
                      : activity.description?.details || 'Actividad registrada'
                    }
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {getRelativeTime(activity.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;